import { FormInstance } from 'antd';
import { Form } from '@common/components';
import { Store } from 'antd/lib/form/interface';
import React from 'react';

import { useMutation } from '@common/hooks';
import { ComplianceItemUploadInput } from '@onboarding/components/compliance-item-files-list';
import { ComplianceItemInputExpirationDate } from '@onboarding/components/compliance-item-input-expiration-date';
import { ComplianceItemType } from '@onboarding/graphql';
import {
  CreateNurseHealthDocMutation,
  UpdateNurseHealthDoc,
} from 'works/legacy/graphql/nurse-health-doc';
import { formatToYearMonthDay } from '@common/services/datetime';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import { createMessage } from 'works/legacy/lib/message-helper';

type Props = {
  form: FormInstance;
  complianceItem: ComplianceItemType;
  refetchQueries: string[];
  onSubmit?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
};

export function ComplianceItemAddAttachmentFormHealthDoc({
  form,
  complianceItem,
  refetchQueries,
  onSubmit,
  onSuccess,
  onError,
}: Props) {
  const { detailItem } = complianceItem;

  const errorMessage = () =>
    createMessage(
      'error',
      'Failed to update health document. Please try again.'
    );

  const [updateNurseHealthDoc, { loading: formSubmitting }] = useMutation(
    UpdateNurseHealthDoc,
    {
      refetchQueries,
      onCompleted: (response) => {
        if (response?.updateNurseHealthDoc.errors) {
          addErrorsToForm(form, response.updateNurseHealthDoc.errors);
          errorMessage();
          onError?.();
        } else {
          onSuccess?.();
          createMessage('success', 'Document has been successfully updated.');
        }
      },
      onError: () => {
        errorMessage();
        onError?.();
      },
    }
  );

  const [createNurseHealthDoc] = useMutation(CreateNurseHealthDocMutation, {
    refetchQueries,
    onCompleted: (response) => {
      if (response?.createNurseHealthDoc.errors) {
        addErrorsToForm(form, response.createNurseHealthDoc.errors);
        errorMessage();
        onError?.();
      } else {
        onSuccess?.();
        createMessage(
          'success',
          'Health Document has been successfully created.'
        );
      }
    },
    onError: () => {
      errorMessage();
      onError?.();
    },
  });

  const onFinish = (values: Store) => {
    if (formSubmitting) return;

    onSubmit?.();

    if (!detailItem) {
      createNurseHealthDoc({
        variables: {
          worksListItemId: complianceItem.id,
          worksProfileId:
            complianceItem.worksList.staffingRequestAssignment.worksProfile.id,
          healthDocId: complianceItem.healthDoc?.id,
          input: {
            expiresOn: formatToYearMonthDay(values.expiresOn),
            completedOn: formatToYearMonthDay(values.completedOn),
          },
          files: values.files ? values.files.fileList : null,
        },
      });

      return;
    }

    updateNurseHealthDoc({
      variables: {
        id: complianceItem.id,
        input: {
          completedOn: formatToYearMonthDay(values.completedOn),
          expiresOn: formatToYearMonthDay(values.expiresOn),
        },
        files: values.files?.fileList ?? [],
      },
    });
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <ComplianceItemInputExpirationDate
        expirationType={complianceItem.expirationType}
      />

      <ComplianceItemUploadInput multiple required />
    </Form>
  );
}
